import React, { useEffect, useState } from "react";
import { Grid, } from "@mui/material";
import { Dialog } from "../../../Components/Dialogs/Dialog";
import { ControlledCustomSelect, OutlinedTextField } from "../../../Components/formComponents";
import { useFormik } from "formik";
import { IReservation, defaultReservation, upsertSingleReservationAsync } from "../../../redux/reservations";
import { IStoreState, useAppDispatch, useAppSelector } from "../../../redux";
import { CustomDatePicker, OutlinedTimePicker } from "../../../Components/formComponents/CustomDatePicker";
import moment from "moment";
import { LayoutSelector } from "../components/LayoutSelector/LayoutSelector";
import { LayoutTableSelector } from "../components/LayoutTableSelector/LayoutTableSelector";


export interface IUpsertReservationDialogProps {
  open: boolean;
  reservation?: IReservation
  onClose: () => void;
  disableTableInfo?: boolean
  onSuccess?: (data: IReservation) => void
}


export const UpsertReservationDialog: React.FC<IUpsertReservationDialogProps> = React.memo((
  props
) => {
  const { open, reservation, disableTableInfo, onSuccess, onClose } = props;
  const dispatch = useAppDispatch();
  const {
    data: tablesLayout,
  } = useAppSelector((storeState: IStoreState) => storeState.tables.single_tables_layout);
  const [tablesList, setTablesList] = useState<{ label: string, value: string }[]>([])


  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    isSubmitting,
    setSubmitting
  } = useFormik({
    initialValues: defaultReservation,
    validate: (values) => {
      const errors: any = {};
      if (!values.user_name) {
        errors.user_name = "*This field is required.";
      }
      if (!values.user_email) {
        errors.user_email = "*This field is required.";
      }
      if (!values.user_phone_no) {
        errors.user_phone_no = "*This field is required.";
      }
      if (!values.reservation_date) {
        errors.reservation_date = "*This field is required.";
      }
      if (!values.reservation_from_time) {
        errors.reservation_from_time = "*This field is required.";
      }
      if (!values.reservation_to_time) {
        errors.reservation_to_time = "*This field is required.";
      }
      if (!values.number_of_people) {
        errors.number_of_people = "*This field is required.";
      }
      if (!values.table_no) {
        errors.table_no = "*This field is required.";
      }
      return errors;
    },
    onSubmit: async (values) => {
      console.log("submit values ===> ", values)
      setSubmitting(true)
      dispatch(
        upsertSingleReservationAsync({
          ...values,
          reservation_from_time: moment(values.reservation_from_time).format("HH:mm"),
          reservation_to_time: moment(values.reservation_to_time).format("HH:mm")
        })
      ).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          onSuccess && onSuccess(values as IReservation)
          onClose()
        }
      }).finally(() => {
        setSubmitting(false)
      });
    },
  });

  useEffect(() => {
    if (!reservation) return
    setValues(reservation)
  }, [reservation])


  useEffect(() => {
    // dispatch(fetchSingleTableLayoutAsync(""))
  }, [])


  useEffect(() => {
    const newList = tablesLayout.tables.filter((table) => table.data.nodeType === "TABLE").map((table) => ({
      label: table.data.table_number.toString(), value: table.data.table_number.toString()
    }))
    setTablesList(newList)
  }, [tablesLayout])



  return (
    <Dialog
      open={open}
      title="Add/Edit Reservation"
      size="sm"
      contentWrappedWithForm={{ onSubmit: handleSubmit }}
      onClose={onClose}
      actions={[
        {
          type: "button",
          variant: "outlined",
          label: "Cancel",
          onClick: onClose,
        },
        {
          type: "submit",
          variant: "contained",
          disabled: isSubmitting,
          label: "Add",
        },
      ]}
    >
      <form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LayoutSelector
              disabled={disableTableInfo}
              value={values.layout_uuid.toString()}
              onChange={(newValue) => {
                setValues({
                  ...values,
                  layout_uuid: newValue.value as string,
                  layout_name: newValue.label
                })
              }}
              error={errors.table_no}
            />
          </Grid>
          <Grid item xs={12}>
            <LayoutTableSelector
              layout={values.layout_uuid}
              disabled={disableTableInfo}
              value={values.table_id.toString()}
              onChange={(newValue) => {
                setValues({
                  ...values,
                  table_id: newValue.value as string,
                  table_no: newValue.label
                })
              }}
              error={errors.table_no}
            />
          </Grid>
          <Grid item xs={12}>
            <OutlinedTextField
              label="Name"
              id="user_name"
              value={values.user_name}
              onChange={handleChange}
              error={errors.user_name}
            />
          </Grid>
          <Grid item xs={12}>
            <OutlinedTextField
              label="Email"
              id="user_email"
              value={values.user_email}
              onChange={handleChange}
              error={errors.user_email}
            />
          </Grid>
          <Grid item xs={12}>
            <OutlinedTextField
              label="Phone Number"
              id="user_phone_no"
              value={values.user_phone_no}
              onChange={handleChange}
              error={errors.user_phone_no}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomDatePicker
              label="Reservation Date"
              fullWidth
              disabled={disableTableInfo}
              fieldName="reservation_date"
              value={values.reservation_date}
              onChange={(newValue, formattedDate) => {
                setFieldValue("reservation_date", formattedDate);
              }}
              error={errors.reservation_date}
            />
          </Grid>
          <Grid item xs={12}>
            <OutlinedTimePicker
              label="From Time"
              value={moment(values.reservation_from_time)}
              fullWidth
              format="hh:mm A"
              onChange={(date) => {
                setValues({
                  ...values,
                  reservation_from_time: date.toISOString(),
                  reservation_to_time: date.add("hour", 1).add("minute", 30).toISOString()
                })
              }}
              error={errors.reservation_from_time}
            />
          </Grid>
          <Grid item xs={12}>
            <OutlinedTimePicker
              label="To Time"
              value={moment(values.reservation_to_time)}
              fullWidth
              format="hh:mm A"
              onChange={(date) => {
                setFieldValue("reservation_to_time", date.toISOString());
              }}
              error={errors.reservation_to_time}
            />
          </Grid>
          <Grid item xs={12}>
            <OutlinedTextField
              label="No. of People"
              id="number_of_people"
              type="number"
              value={values.number_of_people}
              onChange={handleChange}
              error={errors.number_of_people}
            />
          </Grid>
          
        </Grid>
      </form>
    </Dialog>
  );
});
